import type { ICardBuild, ICardConfig } from './types';

import { merge } from '../../helper';
import isEmptyObject from '../../general/isEmpty/isEmptyObject';

import * as exElement from '../../utils/element';
import * as exHeader from '../../utils/header';

import * as exTab from '../tab';

// ----------------------------------------------------------------------------

function create(config: ICardConfig = {}): ICardBuild {
    // Default Config
    const defConfig: ICardConfig = {};

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    const builds = {
        element: {},
    } as ICardBuild;

    // ------------------------------------------------------------------------

    // Create element (card)
    const elCard = exElement.create({
        merge: defConfig,
        class: {
            new: 'card',
        },
    });
    builds.element = elCard;

    // ------------------------------------------------------------------------

    // If using card header
    if (!isEmptyObject(defConfig.cardHeader) || defConfig.cardHeader === true) {
        // Create element (card header)
        const cardHeader = exHeader.create({
            merge: defConfig.cardHeader,
            class: {
                new: 'card-header',
            },
        });
        elCard.appendChild(cardHeader.element);
        builds.header = cardHeader.element;
    }

    // ------------------------------------------------------------------------

    // If using card body
    if (!isEmptyObject(defConfig.cardBody) || defConfig.cardBody === true) {
        // Create element (card body)
        const elCardBody = exElement.create({
            merge: defConfig.cardBody,
            class: {
                new: 'card-body',
            },
        });
        elCard.appendChild(elCardBody);
        builds.body = elCardBody;
    }

    // ------------------------------------------------------------------------

    // If using card footer
    if (!isEmptyObject(defConfig.cardFooter) || defConfig.cardFooter === true) {
        // Create element (card footer)
        const elCardFooter = exElement.create({
            merge: defConfig.cardFooter,
            class: {
                new: 'card-footer',
            },
        });
        elCard.appendChild(elCardFooter);
        builds.footer = elCardFooter;
    }

    // ------------------------------------------------------------------------

    // Settings
    settings(builds, defConfig);

    // ------------------------------------------------------------------------

    // Return
    return builds;
}

function settings(builds: ICardBuild, config: ICardConfig = {}): void {
    const elBody = builds.body;
    const elHeader = builds.header;

    // If using title
    if (config.title && elBody) {
        // If title is string
        if (typeof config.title === 'string') {
            config.title = {
                html: config.title,
            };
        }

        // Create element (card title)
        const elCardTitle = exElement.create({
            merge: config.title,
            tag: 'h5',
            class: {
                new: 'card-title',
            },
        });
        elBody.appendChild(elCardTitle);
        builds.title = elCardTitle;
    }

    // If using text
    if (config.text && elBody) {
        // If text is string
        if (typeof config.text === 'string') {
            config.text = {
                html: config.text,
            };
        }

        // Create element (card text)
        const elCardText = exElement.create({
            merge: config.text,
            tag: 'p',
            class: {
                new: 'card-text',
            },
        });
        elBody.appendChild(elCardText);
        builds.text = elCardText;
    }

    // ------------------------------------------------------------------------

    // If using tab
    if (!isEmptyObject(config.tab) && elHeader && elBody) {
        // Create element (tab)
        const tab = exTab.create({
            merge: config.tab,
            nav: {
                class: {
                    add: 'card-header-tabs',
                },
            },
        });

        elHeader.appendChild(tab.nav);
        elBody.appendChild(tab.tab);
        builds.tab = tab;
    }
}

// ----------------------------------------------------------------------------

export { create };
